import { Add, Clear } from "@mui/icons-material";
import { Box, Button, Chip, Divider, Stack, Typography } from "@mui/material";
import {
  AlertsBarContainer,
  AlertsBarVariant,
  AlertsContext,
} from "@sista/dista-ui";
import { IdmClientContext } from "@sista/idm-client";
import {
  LoadingGuard,
  Profile,
  SistaDialog,
  SistaFormHeader,
  UiContext,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";

export type OrganizationMoveToUnitsDialogProps = {
  profile: Profile;
  organizationId: string;
  parentId?: string;
  onDone: () => unknown;
};

export const OrganizationMoveToUnitsDialog = (
  props: OrganizationMoveToUnitsDialogProps
) => {
  const { closeDialogs } = useContext(UiContext);
  const { t } = useTranslation("ucet");
  const { showSuccess } = useContext(AlertsContext);
  const rej = useRejstrikClient();
  const { loggedAccount } = useContext(IdmClientContext);

  const [getMyOrganizationUnitsCall, getMyOrganizationUnitsState] =
    useSafeSistaAsync(rej.getOrganizationUnitsByProfileId);

  const [getOrganizationUnitsCall, getOrganizationUnitsState] =
    useSafeSistaAsync(rej.getAllOrganizationUnits);

  const [addProfileToUnitCall, addProfileToUnitState] = useSafeSistaAsync(
    rej.addProfileRoleInOrganization
  );

  const [deleteProfileToUnitCall, deleteProfileToUnitState] = useSafeSistaAsync(
    rej.deleteProfileRoleInOrganization
  );

  const pending = useMemo(() => {
    return addProfileToUnitState.pending || deleteProfileToUnitState.pending;
  }, [addProfileToUnitState.pending, deleteProfileToUnitState.pending]);

  const role = "ORGANIZATION_EMPLOYEE";
  const coreData = useMemo(() => {
    return {
      profileId: props.profile.id,
      changeReason: "moving from/to unit",
      minorChange: true,
    };
  }, [props.profile.id]);

  const deleteProfileFromUnitHandler = useCallback(
    async (unitId: string) => {
      const rel = {
        organizationId: unitId,
        role: role,
        data: role,
        ...coreData,
      };
      const result = await deleteProfileToUnitCall(rel);
      if (result) {
        getMyOrganizationUnitsCall({
          profileId: props.profile.id,
          organizationId: props.parentId
            ? props.parentId
            : props.organizationId,
        });
        showSuccess(
          t(
            "ucet.organization.moveToUnits.removedFromUnit",
            "Osoba byla úspěšně vyřazena z organizační jednotky."
          )
        );
      }
    },
    [
      coreData,
      deleteProfileToUnitCall,
      showSuccess,
      t,
      role,
      props.profile.id,
      getMyOrganizationUnitsCall,
      props.parentId,
      props.organizationId,
    ]
  );

  const addProfileToUnitHandler = useCallback(
    async (unitId: string) => {
      const rel = {
        organizationId: unitId,
        data: {
          role: role,
          entityId: props.profile.id,
          entityType: "profiles",
          refEntityId: unitId,
          refEntityType: "organizations",
        },
        ...coreData,
      };
      const result = await addProfileToUnitCall(rel);
      if (typeof result === "object" && result) {
        getMyOrganizationUnitsCall({
          profileId: props.profile.id,
          organizationId: props.parentId
            ? props.parentId
            : props.organizationId,
        });
        showSuccess(
          t(
            "ucet.organization.moveToUnits.addedToUnit",
            "Osoba byla úspěšně zařazena do organizační jednotky."
          )
        );
      }
    },
    [
      coreData,
      addProfileToUnitCall,
      showSuccess,
      t,
      //props.organizationId,
      props.profile.id,
      role,
      getMyOrganizationUnitsCall,
      props.parentId,
      props.organizationId,
    ]
  );

  const possibleUnits = useMemo(() => {
    if (getOrganizationUnitsState.value == null) return [];
    const myUnits = new Set(
      getMyOrganizationUnitsState.value?.map((item) => item.id)
    );
    return getOrganizationUnitsState.value.filter(
      (item) => !myUnits.has(item.id)
    );
  }, [getMyOrganizationUnitsState.value, getOrganizationUnitsState.value]);

  useEffect(() => {
    if (props.profile && props.organizationId) {
      getMyOrganizationUnitsCall({
        profileId: props.profile.id,
        organizationId: props.parentId ? props.parentId : props.organizationId,
      });
      getOrganizationUnitsCall({
        organizationId: props.parentId ? props.parentId : props.organizationId,
      });
    }
  }, [
    props.profile,
    props.organizationId,
    props.parentId,
    getMyOrganizationUnitsCall,
    getOrganizationUnitsCall,
  ]);

  const done = useCallback(() => {
    props.onDone();
    closeDialogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeDialogs, props.onDone]);

  return (
    <SistaDialog
      onClose={done}
      title={t(
        "ucet.organization.moveToUnits.title",
        "Zařazení v rámci organizačních jednotek"
      )}
    >
      <SistaFormHeader
        title={props.profile?.firstName + " " + props.profile?.lastName}
      />
      <Stack direction={"column"} spacing={2} sx={{ padding: 2 }}>
        <Typography variant="body1">
          {t(
            "ucet.organization.moveToUnitsDescription.text",
            "Osobu je možné zařadit do níže uvedených organizačních jednotek, které jsou součástí Vaší organizace. V případě, že osoba nebude zařazena do žádné organizační jednotky, bude evidována v seznamu uživatelů na úrovni celé organizace."
          )}
        </Typography>
        <AlertsBarContainer
          id="alerts-in-dialog"
          variant={AlertsBarVariant.relative}
        />
        <Divider />
        <Typography variant="h6">
          {t(
            "ucet.organization.moveToUnits.personInUnits",
            "Osoba je zařazena do organizačních jednotek: "
          )}
        </Typography>
        <LoadingGuard state={getMyOrganizationUnitsState}>
          {getMyOrganizationUnitsState.value?.length === 0 && (
            <Typography>
              {t(
                "ucet.organization.moveToUnits.noUnits",
                "Uživatel není v žádné organizační jednotce."
              )}
            </Typography>
          )}
          <Box>
            {getMyOrganizationUnitsState.value?.map((unit) => (
              <span key={unit.id}>
                <Chip
                  label={unit.displayName}
                  onDelete={() => {
                    console.log("delete unit", unit.id);
                    return !pending && deleteProfileFromUnitHandler(unit.id);
                  }}
                  deleteIcon={<Clear />}
                  sx={{ margin: 1 }}
                />
              </span>
            ))}
          </Box>
        </LoadingGuard>
        <Divider />
        <Typography variant="h6">
          {t(
            "ucet.organization.moveToUnits.possibleUnits",
            "Dostupné org. jednotky k zařazení: "
          )}
        </Typography>
        <LoadingGuard state={getOrganizationUnitsState}>
          {possibleUnits?.length === 0 && (
            <Typography>
              {t(
                "ucet.organization.moveToUnits.noPossibleUnits",
                "Organizace nemá žádné/další organizační jednotky."
              )}
            </Typography>
          )}
          <Box>
            {possibleUnits?.map((unit) => (
              <span key={unit.id}>
                <Chip
                  label={unit.displayName}
                  onDelete={() => {
                    console.log("add unit", unit.id);
                    return !pending && addProfileToUnitHandler(unit.id);
                  }}
                  deleteIcon={<Add />}
                  sx={{ margin: 1 }}
                />
              </span>
            ))}
          </Box>
        </LoadingGuard>
        <Stack direction={"row"} spacing={2} justifyContent={"flex-end"}>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" onClick={done}>
            {t("ucet.organization.moveToUnits.done", "Hotovo")}
          </Button>
        </Stack>
      </Stack>
    </SistaDialog>
  );
};
